import React, { Component, createRef } from "react";
import axios from 'axios';
import { baseUrl, decodeJwt, UserRole, rolenum, ComplianceFlag } from '../Pages/ApiCommonService';
import {
  Button,
  Select,
  Grid,
  MenuItem,
  IconButton,
  SelectChangeEvent,
  Snackbar,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Alert,
  Autocomplete,
  TextField,
} from "@mui/material";

import { fetchCSRUsersByBroker, GetAllCsrUserConfig, } from "../ApiSevices/UserMasterApis";
import CloseIcon from "@mui/icons-material/Close";
import {
  fetchAllLobs,
  fetchAllChecklistType, createIdForFileUploaded, createJobidForAfterFileUpload
} from "../ApiSevices/FileUploadApis";
import { fetchBrokerFileUploadLImit } from "../ApiSevices/ExdionAdminApi";
import { expTokenProcessAndGenerate } from "../ApiSevices/AuthApis";
import { GridTable1 } from "../Sidebar/SidebarStyles";

interface UploadedFile {
  name: string;
  file: File;
  docType: string;
}

interface TableProps {
  displaysidebar: boolean;
  normalUpload?: boolean;
  basePath?: string;
  isMSUser?: boolean;
  selectedBrokerId?: number;
  selectedCSRUserId?: number;
  selectedCSRUser?: string;
  selectedSecondaryCSRUserId?: number;
  complianceFlag?: number;
}

interface CSR {
  ID: number;
  vchEmail: string;
}

interface State {
  jobId: any;
  JobId: any;
  successfullyUploadedFields: any;
  successfullyUploadedDocs: any;
  Role_Id: any;
  showPrimaryUser: boolean;
  csrs: CSR[];
  selectedPrimaryUser: number;
  selectedSecondaryUser: number;
  uploadedFiles: {
    [key: string]: UploadedFile[];
  };

  selectedCoverage: string;
  lobCodes: {
    Lob_Name: string;
    Lob_Code: string;
  }[];

  checklistTypes: {
    ID: string;
    VCH_CHECKLIST: string;
  }[];

  selectedChecklist: string;
  remarks: string;
  clientName: string;
  corruptedFiles: any;
  emptyFiles: any;
  errors: {
    lineOfCoverage: string;
    checklist: string;
    currentTermPolicy: string;
    sourceDoc: string;
    duplicateFiles: string;
    primaryUser: string;
    secondaryUser: string;
  };

  snackbarOpen: boolean;
  snackbarMessage: string;
  snackbarSeverity: "success" | "error" | "info" | "warning";
  loading: boolean;
  isMandatory: boolean;
  mandatoryMessage: string;
  lockedFieldsMessage: string;
}

/*
    File validation
*/
const validateFileUpload = (
  fieldName: string,
  file: File,
  existingFiles: UploadedFile[],
) => {
  const pdfOnlyFields = ["Current Term Policy"];

  const extensionNameMap: Record<string, string> = {
    pdf: "PDF",
    doc: "Word",
    docx: "Word",
    xlsx: "Excel",
    xls: "Excel",
  };

  let allowedFormats: string[] = [];

  /*
        Same validation as existing component
        Acord Application:
        PDF + Excel + Word
    */
  if (fieldName === "Acord Application") {
    allowedFormats = ["pdf", "xlsx", "xls", "doc", "docx"];
  } else if (pdfOnlyFields.includes(fieldName)) {
    allowedFormats = ["pdf"];
  }

  const fileExtension = file?.name?.split(".").pop()?.toLowerCase();

  if (!fileExtension || !allowedFormats.includes(fileExtension)) {
    const namesSet = new Set<string>();

    for (const extension of allowedFormats) {
      const name = extensionNameMap[extension];

      if (name) {
        namesSet.add(name);
      }
    }

    const typeNames = Array.from(namesSet);
    const last = typeNames.pop();

    const readable =
      typeNames.length > 0 ? `${typeNames.join(", ")} and ${last}` : last;
    return `Only ${readable} files are allowed`;
  }

  /*
        Only one file allowed
        for both documents
    */
  const singleFileFields = ["Current Term Policy", "Acord Application"];

  if (singleFileFields.includes(fieldName) && existingFiles.length > 0) {
    const displayName =
      fieldName === "Acord Application" ? "Application Accord" : fieldName;
    return `${displayName} allows only a single file`;
  }

  return null;
};

class EndorsementCheckUploads extends Component<TableProps, State> {
  private fileInputRefs: Record<string, React.RefObject<HTMLInputElement>>;
  docTypeArray: Record<string, number> = { "Current Term Policy": 1, "Acord Application": 5 }

  constructor(props: TableProps) {
    super(props);

    this.state = {
      jobId: null,
      JobId: null,
      uploadedFiles: {},
      successfullyUploadedFields: {},
      successfullyUploadedDocs: {},
      selectedCoverage: "",
      lobCodes: [],
      checklistTypes: [],
      corruptedFiles: {},
      emptyFiles: {},
      selectedChecklist: "",
      remarks: "",
      Role_Id: '',
      showPrimaryUser: false,
      csrs: [],
      selectedPrimaryUser: 0,
      selectedSecondaryUser: 0,
      clientName: "",
      errors: {
        lineOfCoverage: "",
        checklist: "",
        currentTermPolicy: "",
        sourceDoc: "",
        duplicateFiles: "",
        primaryUser: "",
        secondaryUser: "",
      },
      snackbarOpen: false,
      snackbarMessage: "",
      snackbarSeverity: "error",
      loading: false,
      isMandatory: false,
      mandatoryMessage: "",
      lockedFieldsMessage: ""
    };
    this.fileInputRefs = {};
  }

  async componentDidMount() {
    this.setState({
      loading: true,
    });

    try {
      const token = sessionStorage.getItem("token");

      let brokerId: number = 0;
      let roleId: number = 0;
      let emailId = "";

      if (token) {
        const decodedToken = decodeJwt(token);

        if (decodedToken) {
          roleId = decodedToken.RoleId;
          brokerId = decodedToken.BrokerId;
          emailId = decodedToken.Email;

          this.setState({
            Role_Id: decodedToken.RoleId,
          });
        }
      }

      await Promise.all([
        this.fetchLobs(),
        this.fetchAllChecklistTypes(),
        this.fetchUsers(brokerId, roleId, emailId),
      ]);
    } catch (error) {
      console.error("Error loading dropdown data:", error);
    } finally {
      this.setState({
        loading: false,
      });
    }
  }

  async fetchUsers(brokerId: number, roleId: number, userName?: string,) {
    try {
      const response = await GetAllCsrUserConfig();

      const brokerIds =
        response?.data
          ?.filter((e: any) => e.IsActive)
          ?.map((e: any) => e.BrokerId) || [];

      if (
        brokerIds.includes(brokerId) &&
        (
          roleId === rolenum.BrokerAdmin ||
          roleId === rolenum.CSR
        )
      ) {
        const csrUsers = await fetchCSRUsersByBroker(
          brokerId,
        );

        this.setState({
          csrs: csrUsers || [],
          showPrimaryUser: true,
        });
      }
    } catch (error) {
      console.error(
        "Error fetching CSR users:",
        error,
      );

      this.setState({
        snackbarOpen: true,
        snackbarMessage: "Error loading CSR users",
        snackbarSeverity: "error",
      });
    }
  }

  fetchLobs = async () => {
    try {
        const fetchResponse = await fetchAllLobs();

        if (fetchResponse && fetchResponse.length > 0) {
            const isPersonalLine = window.location.pathname
                .toLowerCase()
                .includes("/personalline/");

            const filteredLobs = fetchResponse.filter((item: any) => {
                const lobName =
                    item?.Lob_Name?.trim().toLowerCase() || "";

                const isPersonalCoverage =
                    lobName.startsWith("personal") ||
                    lobName === "homeowners";

                if (isPersonalLine) {
                    return isPersonalCoverage;
                }

                return !isPersonalCoverage;
            });

            this.setState({
                lobCodes: filteredLobs
            });
        }
    } catch (error) {
        console.error("Error fetching LOB data:", error);
    }
  };

  fetchAllChecklistTypes = async () => {
    try {
      const fetchResponse = await fetchAllChecklistType();

      if (fetchResponse && fetchResponse.length > 0) {
        const checklistTypes = fetchResponse as {
          ID: string;
          VCH_CHECKLIST: string;
        }[];

        this.setState({
          checklistTypes,
        });
      }
    } catch (error) {
      console.error("Error fetching checklist types:", error);
    }
  };

  showSnackbar = (
    message: string,
    severity: "success" | "error" | "info" | "warning",
  ) => {
    this.setState({
      snackbarOpen: true,
      snackbarMessage: message,
      snackbarSeverity: severity,
    });
  };

  handleCoverageChange = (event: SelectChangeEvent<string>) => {
    this.setState((prevState) => ({
      selectedCoverage: event.target.value,
      errors: {
        ...prevState.errors,
        lineOfCoverage: "",
      },
    }));
  };

  handleChecklistChange = (event: SelectChangeEvent<string>) => {
    this.setState((prevState) => ({
      selectedChecklist: event.target.value,
      errors: {
        ...prevState.errors,
        checklist: "",
      },
    }));
  };

  handleRemarksChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      remarks: event.target.value,
    });
  };

  handleClientNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      clientName: event.target.value,
    });
  };

  handleUserSelectionChange = (event: any) => {
    const value = Number(event?.id) || 0;

    this.setState((prevState) => ({
      selectedPrimaryUser: value,
      errors: {
        ...prevState.errors,
        primaryUser: "",
      },
    }));
  };

  handleSecondaryUserSelectionChange = (event: any) => {
    const value = Number(event?.id) || 0;

    this.setState((prevState) => ({
      selectedSecondaryUser: value,
      errors: {
        ...prevState.errors,
        secondaryUser: "",
      },
    }));
  };

  handleFileUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldName: string,
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }
    let newFiles: UploadedFile[] = [];

    const file = files[0];
    const existingFiles = this.state.uploadedFiles[fieldName] || [];
    const duplicateFile = existingFiles.some((item) => item.name === file.name);

    if (duplicateFile) {
      this.showSnackbar(
        "This file has already been selected.",
        "error",
      );
      event.target.value = "";
      return;
    }

    const errorMessage = validateFileUpload(
      fieldName,
      file,
      existingFiles,
    );

    if (errorMessage) {
      this.showSnackbar(
        errorMessage,
        "error",
      );

      event.target.value = "";
      return;
    }

    const docType = String(this.docTypeArray[fieldName] || "");
    newFiles.push({ name: file.name, file, docType });

    this.setState((prevState) => ({
      uploadedFiles: {
        ...prevState.uploadedFiles,
        [fieldName]: newFiles,
      },

      errors: {
        ...prevState.errors,
        currentTermPolicy:
          fieldName === "Current Term Policy"
            ? ""
            : prevState.errors.currentTermPolicy,
        sourceDoc:
          fieldName === "Acord Application" ? "" : prevState.errors.sourceDoc,
      },
    }));

    event.target.value = "";
  };

  handleDrop = (event: React.DragEvent<HTMLDivElement>, fieldName: string) => {
    event.preventDefault();
    const files = event.dataTransfer.files;

    if (!files || files.length === 0) {
      return;
    }

    if (files.length > 1) {
      const displayName =
        fieldName === "Acord Application" ? "Application Accord" : fieldName;

      this.showSnackbar(
        `Only one file is allowed for ${displayName}.`,
        "error",
      );
      return;
    }

    const file = files[0];
    const existingFiles = this.state.uploadedFiles[fieldName] || [];
    const duplicateFile = existingFiles.some((item) => item.name === file.name);

    if (duplicateFile) {
      this.showSnackbar(
        "This file has already been selected.",
        "error",
      );

      return;
    }

    const errorMessage = validateFileUpload(
      fieldName,
      file,
      existingFiles,
    );

    if (errorMessage) {
      this.showSnackbar(
        errorMessage,
        "error",
      );
      return;
    }

    const docType = String(this.docTypeArray[fieldName] || "");
    const newFile: UploadedFile = {
      name: file.name,
      file,
      docType
    };

    this.setState((prevState) => ({
      uploadedFiles: {
        ...prevState.uploadedFiles,
        [fieldName]: [newFile],
      },

      errors: {
        ...prevState.errors,
        currentTermPolicy:
          fieldName === "Current Term Policy"
            ? ""
            : prevState.errors.currentTermPolicy,

        sourceDoc:
          fieldName === "Acord Application" ? "" : prevState.errors.sourceDoc,
      },
    }));
  };

  handleFileRemove = (fieldName: string) => {
    this.setState((prevState) => {
      const updatedFiles = {
        ...prevState.uploadedFiles,
      };
      delete updatedFiles[fieldName];

      return {
        uploadedFiles: updatedFiles,
      };
    });

    if (this.fileInputRefs[fieldName]?.current) {
      this.fileInputRefs[fieldName].current!.value = "";
    }
  };

  validateForm = () => {
    const {
      selectedCoverage,
      selectedChecklist,
      uploadedFiles,
      showPrimaryUser,
      selectedPrimaryUser,
      selectedSecondaryUser,
    } = this.state;

    const errors = {
      lineOfCoverage: "",
      checklist: "",
      currentTermPolicy: "",
      sourceDoc: "",
      duplicateFiles: "",
      primaryUser: "",
      secondaryUser: "",
    };

    if (!selectedCoverage) {
      errors.lineOfCoverage = "Line of Coverage is mandatory*";
    }

    if (!selectedChecklist) {
      errors.checklist = "Checklist is mandatory*";
    }

    if (
      !uploadedFiles["Current Term Policy"] ||
      uploadedFiles["Current Term Policy"].length === 0
    ) {
      errors.currentTermPolicy = "Current Term Policy is mandatory*";
    }

    if (
      !uploadedFiles["Acord Application"] ||
      uploadedFiles["Acord Application"].length === 0
    ) {
      errors.sourceDoc = "Accord Application is mandatory*";
    }

    this.setState({
      errors,
    });

    if (Object.values(errors).some((error) => error !== "")) {
      return false;
    }

    const currentTermFile = uploadedFiles["Current Term Policy"]?.[0];
    const applicationFile = uploadedFiles["Acord Application"]?.[0];

    if (
      currentTermFile &&
      applicationFile &&
      currentTermFile.name === applicationFile.name
    ) {
      this.showSnackbar(
        "Duplicate document uploaded. Please select different files.",
        "error",
      );
      return false;
    }

    if (showPrimaryUser && selectedPrimaryUser && selectedSecondaryUser && selectedPrimaryUser === selectedSecondaryUser) {
      this.showSnackbar(
        "Please select different Primary and Secondary Users",
        "error",
      );

      return false;
    }

    return true;
  };

  handleSubmit = async () => {
    if (this.validateForm()) {
      const {
        selectedCoverage,
        selectedChecklist,
        remarks,
        clientName,
        uploadedFiles,
        jobId,
        JobId,
        successfullyUploadedDocs,
        Role_Id,
      } = this.state;
      const { selectedBrokerId, selectedCSRUserId, selectedSecondaryCSRUserId } = this.props;

      Object.keys(uploadedFiles).forEach((fieldName) => {
        const fileArray = uploadedFiles[fieldName];
        if (Array.isArray(fileArray)) {
          uploadedFiles[fieldName] = fileArray.map(({ name, file, docType }) => {
            const lastDotIndex = name.lastIndexOf(".");
            let cleanedName = name;

            if (lastDotIndex > 0) {
              const nameWithoutExt = name.slice(0, lastDotIndex);
              const extension = name.slice(lastDotIndex + 1);

              const sanitizedName = nameWithoutExt.replace(/\./g, "");
              cleanedName = `${sanitizedName}.${extension}`;
            }

            return { name: cleanedName, file, docType };
          });
        }
      });

      const token = sessionStorage.getItem("token");
      this.setState({ loading: true });
      try {
        const Token = await expTokenProcessAndGenerate(token);
        const decodedToken = decodeJwt(token);
        let jobIdToUse = jobId;
        let jobIdValue = JobId;
        let createIdResponse = null;

        if (!jobIdToUse) {
          const brokerId = selectedBrokerId || decodedToken?.BrokerId;
          const userId = selectedCSRUserId;
          const secondaryUserId = selectedSecondaryCSRUserId;
          createIdResponse = await createIdForFileUploaded(
            {
              JobTypeId: 2,
              LobCode: selectedCoverage,
              MacroFile: selectedChecklist,
              ClientName: clientName,
              Remarks: remarks,
              MisUser: false,
              brokerId: brokerId,
              userId: userId,
              secondaryUserId: secondaryUserId,
              ComplianceFlag: this.props.complianceFlag ?? 0,
            }
          );
          const responseData = createIdResponse?.data;

          if (responseData?.status === "success") {
            jobIdToUse = responseData?.data?.Id;
            jobIdValue = responseData?.data?.JobId;

            this.setState({
              jobId: jobIdToUse,
              JobId: jobIdValue,
              snackbarOpen: true,
              snackbarMessage: responseData?.message || "Job created successfully",
              snackbarSeverity: "success"
            });
          } else {
            this.setState({
              snackbarOpen: true,
              snackbarMessage: "Job creation failed",
              snackbarSeverity: "error"
            });
          }
        }

        const getBrokerId = Number(jobIdValue?.slice(0, 4))
        const brokerFileSizeRestriction = await fetchBrokerFileUploadLImit(getBrokerId);
        const DEFAULT_FILE_SIZE_MB = 80;

        const maxFileSizeMB = brokerFileSizeRestriction && brokerFileSizeRestriction?.INT_FILE_SIZE
          ? brokerFileSizeRestriction?.INT_FILE_SIZE
          : DEFAULT_FILE_SIZE_MB;
        const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;

        const uploadPromises = [];

        const newSuccessfullyUploadedDocs = { ...successfullyUploadedDocs };
        const previouslyUploadedFileNames: any = {};

        for (const field in newSuccessfullyUploadedDocs) {
          previouslyUploadedFileNames[field] =
            newSuccessfullyUploadedDocs[field]?.map(
              (item: any) => item?.fileName
            ) || [];
        }

        const oversizedFiles: string[] = [];
        for (const key in uploadedFiles) {
          const uploadedFile = uploadedFiles[key];
          if (!uploadedFile) continue;

          for (const { file } of uploadedFile) {
            if (!file) continue;
            if (file.size > maxFileSizeBytes) {
              oversizedFiles.push(file.name);
            }
          }
        }

        if (oversizedFiles.length > 0) {
          alert(
            `Exceeds the allowed size limit of ${maxFileSizeMB} MB for this BrokerId:\n\n${oversizedFiles.join(
              ", "
            )}\n\nPlease remove them and try again.`
          );
          this.setState({ loading: false });
          return;
        }

        if (uploadedFiles) {
          for (const key in uploadedFiles) {
            const uploadedFile = uploadedFiles[key];
            if (!uploadedFile) continue;

            for (const { file, docType } of uploadedFile) {
              if (!file) continue;

              const alreadyUploadedFileNames = previouslyUploadedFileNames?.[key] || [];
              const isAlreadyUploaded = alreadyUploadedFileNames?.includes(file?.name);

              if (isAlreadyUploaded) {
                console.log(`Skipping already uploaded docType ${docType} for field ${key}`);
                continue;
              }
              const formData = new FormData();
              formData.append("files", file);
              formData.append("jobid", jobIdToUse?.toString());
              formData.append("documenttype", docType.toString());

              const uploadPromise = axios
                .post(
                  `${baseUrl}api/Upload/UploadPdfs?documenttype=${docType}&jobid=${jobIdToUse}`,
                  formData,
                  {
                    headers: {
                      Authorization: `Bearer ${Token}`,
                      "Content-Type": "multipart/form-data",
                    },
                  }
                )
                .then((response: any) => ({
                  response,
                  docType,
                  key,
                  fileName: file.name,
                }))
                .catch((error: any) => {
                  const status = error?.response?.status || 500;
                  const message = error?.response?.data?.detail || "Unknown error occurred";

                  let userMessage;
                  switch (status) {
                    case 400:
                      userMessage = "Invalid file type. Please upload a supported format.";
                      break;
                    case 404:
                      userMessage = "Job ID not found. Please create a job before uploading.";
                      break;
                    case 415:
                      userMessage = "File is corrupted or unreadable.";
                      break;
                    case 502:
                      userMessage = "Upload failed due to S3 service issue. Try again later.";
                      break;
                    default:
                      userMessage = `Upload failed: ${message}`;
                  }

                  this.setState({
                    snackbarOpen: true,
                    snackbarMessage: userMessage,
                    snackbarSeverity: "error",
                    loading: false,
                  });
                  return {
                    response: null,
                    docType,
                    key,
                    fileName: file.name,
                    failed: true,
                  };
                });

              uploadPromises.push(uploadPromise);
            }
          }
          const uploadResults = await Promise.allSettled(uploadPromises);

          const corruptedDocTypes: any = [];
          const emptyDocTypes: any = [];

          const corruptedFilesMap: any = {};
          const emptyFilesMap: any = {};

          uploadResults.forEach((result: any) => {
            if (result.status === "fulfilled") {
              const { response, docType, key, fileName } = result.value;

              if (response?.status === 200) {
                return;
              } else {
                corruptedFilesMap[key] = corruptedFilesMap[key] || [];
                corruptedFilesMap[key].push({ fileName, docType });
              }
            } else if (result.status === "rejected") {
              const errorInfo = result.reason || {};
              const { key, fileName, docType } = errorInfo;

              corruptedFilesMap[key] = corruptedFilesMap[key] || [];
              corruptedFilesMap[key].push({ fileName, docType });
            }
          });

          // Re-fetch filesToUpload here to avoid stale keys issue ===
          const latestFilesToUpload = this.state.uploadedFiles;

          const updatedSuccessfulFields: any = {};
          const updatedSuccessfulDocs: any = {};

          if (latestFilesToUpload && typeof latestFilesToUpload === "object") {
            for (const key in latestFilesToUpload) {
              const filesAry = latestFilesToUpload[key];
              if (!Array.isArray(filesAry)) continue;

              filesAry.forEach((doc) => {
                const isCorrupted =
                  corruptedFilesMap[key]?.some((f: any) => f.fileName === doc.file.name) || false;
                const isEmpty =
                  emptyFilesMap[key]?.some((f: any) => f.fileName === doc.file.name) || false;

                // Lock only the successful files
                if (!isCorrupted && !isEmpty) {
                  updatedSuccessfulFields[key] = updatedSuccessfulFields[key] || {};
                  updatedSuccessfulFields[key][doc.file.name] = true;

                  updatedSuccessfulDocs[key] = [
                    ...(updatedSuccessfulDocs[key] || []),
                    {
                      docType: Number(doc.docType),
                      fileName: doc?.file.name,
                    },
                  ];
                }
              });
            }
          }

          const lockedFieldsMessage = `The files are successfully uploaded and field is locked.`;

          this.setState((prevState) => ({
            successfullyUploadedFields: {
              ...prevState.successfullyUploadedFields,
              ...updatedSuccessfulFields,
            },

            successfullyUploadedDocs: {
              ...prevState.successfullyUploadedDocs,
              ...updatedSuccessfulDocs,
            },

            lockedFieldsMessage,
          }));

          if (corruptedDocTypes.length > 0 || emptyDocTypes.length > 0) {
            const corruptedFields = Object.keys(this.docTypeArray).filter((key) =>
              corruptedDocTypes.includes(this.docTypeArray[key])
            );
            const emptyFields = Object.keys(this.docTypeArray).filter((key) =>
              emptyDocTypes.includes(this.docTypeArray[key])
            );

            const messages: string[] = [];

            // Add corrupted file names to message
            if (corruptedFields.length > 0) {
              const corruptedFileNames = Object.values(corruptedFilesMap)
                .flat()
                .map((f: any) => f.fileName);
              messages.push(
                `${corruptedFileNames.join(", ")} Error processing PDF file: Stream has ended unexpectedly`
              );
            }

            // Add empty file names to message
            if (emptyFields.length > 0) {
              const emptyFileNames = Object.values(emptyFilesMap)
                .flat()
                .map((f: any) => f.fileName);
              messages.push(`${emptyFileNames.join(", ")} File is empty or contains no text`);
            }
            this.setState((prevState) => ({
              corruptedFiles: {
                ...prevState.corruptedFiles,
                ...corruptedFilesMap,
              },

              emptyFiles: {
                ...prevState.emptyFiles,
                ...emptyFilesMap,
              },

              snackbarOpen: true,
              snackbarMessage: messages.join(" "),
            }));

            return;
          }
          if (uploadResults?.every(
            (r) => r.status === "fulfilled" && r.value.response?.status === 200
          )) {
            try {
              const jobIdResponse = await createJobidForAfterFileUpload(jobIdToUse);
              const { basePath } = this.props;
              if (jobIdResponse?.status === 200) {
                const jobTypeId =
                  createIdResponse?.data?.data?.JobTypeId === 1
                    ? "Automatic Renewal"
                    : "Marketed Renewal";

                alert(`${jobTypeId} Files Uploaded Successfully for Job ID: ${jobIdValue}`);
                await this.handleReset();

                setTimeout(() => {
                  if ([UserRole?.CSR, UserRole?.BrokerAdmin]?.includes(Role_Id)) {
                    window.location.href = basePath + "/jobs/jobcsrlisting";
                  } else {
                    window.location.href = basePath + "/jobs/joblisting";
                  }
                }, 2000);
              }
            } catch (error) {
              console.error("Error in creating job:", error);
            }
          }
        }
      } catch (error) {
        console.error("Error in jobId creation API calls:", error);
      } finally {
        this.setState({ loading: false });
      }
    }
  };

  handleReset = () => {
    this.setState({
      uploadedFiles: {},
      selectedCoverage: "",
      selectedChecklist: "",
      remarks: "",
      clientName: "",
      selectedPrimaryUser: 0,
      selectedSecondaryUser: 0,

      errors: {
        lineOfCoverage: "",
        checklist: "",
        currentTermPolicy: "",
        sourceDoc: "",
        duplicateFiles: "",
        primaryUser: "",
        secondaryUser: "",
      },
    });

    Object.keys(this.fileInputRefs).forEach((key) => {
      if (this.fileInputRefs[key]?.current) {
        this.fileInputRefs[key].current!.value = "";
      }
    });
  };

  handleCloseSnackbar = () => {
    this.setState({
      snackbarOpen: false,
    });
  };

  handleCloseConfirmDialog = () => {
    this.setState({
      isMandatory: false,

      mandatoryMessage: "",
    });
  };

  renderFileUpload = (
    fieldName: string,
    label: string,
    isRequired: boolean = false,
  ) => {
    if (!this.fileInputRefs[fieldName]) {
      this.fileInputRefs[fieldName] = createRef();
    }

    const files = this.state.uploadedFiles[fieldName] || [];

    return (
      <fieldset
        style={{
          border: "1px solid #ddd",
          padding: "10px",
          borderRadius: "5px",
          minHeight: "70px",
        }}
      >
        <div
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => this.handleDrop(event, fieldName)}
          style={{
            border: "2px dashed #ccc",

            padding: "10px",

            borderRadius: "5px",

            backgroundColor: "#fafafa",
          }}
        >
          <Grid container spacing={1} alignItems="center">
            <Grid item lg={12} sm={12}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "12px",
                }}
              >
                {label}{" "}
                {isRequired && (
                  <span
                    style={{
                      color: "red",

                      fontSize: "14px",

                      fontWeight: "bold",
                    }}
                  >
                    *
                  </span>
                )}
              </Typography>
            </Grid>

            <Grid item>
              <Button
                variant="contained"
                component="label"
                size="small"
                sx={{
                  fontSize: "8px",
                  color: "black",
                  border: "1px solid black",
                  backgroundColor: "lightgray",
                }}
              >
                Choose File
                <input
                  type="file"
                  hidden
                  multiple={false}
                  ref={this.fileInputRefs[fieldName]}
                  onChange={(event) =>
                    this.handleFileUpload(
                      event,
                      fieldName,
                    )
                  }
                />
              </Button>
            </Grid>

            <Grid item xs>
              {files.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "10px",
                    }}
                  >
                    {file.name}
                  </Typography>

                  <IconButton
                    size="small"
                    onClick={() => this.handleFileRemove(fieldName)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Grid>
          </Grid>
        </div>
      </fieldset>
    );
  };

  render() {
    const { selectedCoverage, selectedChecklist, remarks, clientName, errors, snackbarOpen, snackbarMessage, snackbarSeverity, loading, lobCodes, checklistTypes, isMandatory, mandatoryMessage, showPrimaryUser, csrs, selectedPrimaryUser, selectedSecondaryUser, } = this.state;
    const { displaysidebar } = this.props;

    return (
      <>
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: -60,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(255,255,255,0.5)",
              zIndex: 9999,
            }}
          >
            <div className="loader" />
          </Box>
        )}

        <GridTable1 displaysidebar={displaysidebar}>
          <div
            style={{
              padding: "0px",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "14px",
                fontWeight: "bold",
                padding: "8px 0",
              }}
            >
              ENDORSEMENT CHECKING
            </Typography>

            <div
              style={{
                height: "80vh",
                overflowY: "auto",
                paddingRight: "8px",
              }}
            >
              <Grid
                container
                spacing={1}
                sx={{
                  display: "flex",
                  flexDirection: {
                    xs: "column",
                    lg: "row",
                  },

                  marginTop: 0.5,
                }}
              >
                {/* LEFT COLUMN */}

                <Grid item lg={displaysidebar ? 4.6 : 5.3} sm={12}>
                  {showPrimaryUser && (
                    <Grid item lg={12} sm={12} marginTop={1}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "12px",
                        }}
                      >
                        Primary User
                      </Typography>

                      <Autocomplete
                        options={csrs.map((user) => ({
                          label: user.vchEmail,
                          id: user.ID,
                        }))}
                        size="small"
                        getOptionLabel={(option) =>
                          option?.label || ""
                        }
                        value={
                          csrs
                            .filter(
                              (user) =>
                                user.ID === selectedPrimaryUser,
                            )
                            .map((user) => ({
                              label: user.vchEmail,
                              id: user.ID,
                            }))[0] || null
                        }
                        onChange={(_event, newValue) =>
                          this.handleUserSelectionChange(
                            newValue,
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Select User..."
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              style: {
                                height: 30,
                                fontSize: "11px",
                              },
                            }}
                          />
                        )}
                        fullWidth
                        slotProps={{
                          listbox: {
                            sx: {
                              fontSize: "11px",
                            },
                          },
                        }}
                      />

                      {errors.primaryUser && (
                        <span
                          style={{
                            color: "red",
                            fontSize: "smaller",
                          }}
                        >
                          {errors.primaryUser}
                        </span>
                      )}
                    </Grid>
                  )}
                  {/* Line of Coverage */}

                  <Grid item lg={12} sm={12} marginTop={1}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "12px",
                      }}
                    >
                      Line of Coverage{" "}
                      <span
                        style={{
                          color: "red",

                          fontSize: "14px",

                          fontWeight: "bold",
                        }}
                      >
                        *
                      </span>
                    </Typography>

                    <Select
                      id="coverage"
                      value={selectedCoverage}
                      onChange={this.handleCoverageChange}
                      displayEmpty
                      fullWidth
                      sx={{
                        height: "30px",

                        "& .MuiOutlinedInput-input": {
                          fontSize: "12px",
                        },
                      }}
                    >
                      {lobCodes.map((item, index) => (
                        <MenuItem
                          key={index}
                          value={item.Lob_Code}
                          sx={{
                            fontSize: "12px",
                          }}
                        >
                          {item.Lob_Name}
                        </MenuItem>
                      ))}
                    </Select>

                    {errors.lineOfCoverage && (
                      <span
                        style={{
                          color: "red",

                          fontSize: "smaller",
                        }}
                      >
                        {errors.lineOfCoverage}
                      </span>
                    )}
                  </Grid>

                  {/* Remarks */}

                  <Grid item lg={12} sm={12} marginTop={0.5}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "12px",
                      }}
                    >
                      Remarks
                    </Typography>

                    <input
                      type="text"
                      value={remarks}
                      onChange={this.handleRemarksChange}
                      placeholder="Remarks"
                      maxLength={150}
                      style={{
                        width: "100%",
                        height: "30px",
                        fontSize: "12px",
                        padding: "5px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        outline: "none",
                      }}
                    />

                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "10px",
                        color: "gray",
                      }}
                    >
                      Maximum {remarks.length}/150 Characters
                    </Typography>
                  </Grid>

                  {/* Endorsement Check */}
                  <Grid
                    item
                    lg={12}
                    sm={12}
                    marginTop={0.5}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "12px"
                      }}
                    >
                      Endorsement{" "}
                      <span
                        style={{
                          color: "red",
                          fontSize: "14px",
                          fontWeight: "bold"
                        }}
                      >
                        *
                      </span>
                    </Typography>

                    {this.renderFileUpload(
                      "Current Term Policy",
                      "Current Term Policy",
                      true
                    )}

                    {errors.currentTermPolicy && (
                      <span
                        style={{
                          color: "red",
                          fontSize: "smaller"
                        }}
                      >
                        {errors.currentTermPolicy}
                      </span>
                    )}
                  </Grid>

                  {/* Select Source Document */}
                  <Grid
                    item
                    lg={12}
                    sm={12}
                    marginTop={0.5}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "12px"
                      }}
                    >
                      Source Document{" "}
                      <span
                        style={{
                          color: "red",
                          fontSize: "14px",
                          fontWeight: "bold"
                        }}
                      >
                        *
                      </span>
                    </Typography>

                    {this.renderFileUpload(
                      "Acord Application",
                      "Accord Application",
                      true
                    )}

                    {errors.sourceDoc && (
                      <span
                        style={{
                          color: "red",
                          fontSize: "smaller"
                        }}
                      >
                        {errors.sourceDoc}
                      </span>
                    )}
                  </Grid>

                  {/* BUTTONS */}

                  <Grid item lg={12} sm={12} marginTop={2}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item>
                        <Button
                          variant="contained"
                          size="small"
                          sx={{
                            fontSize: "10px",
                            backgroundColor: "#501e75",
                          }}
                          onClick={this.handleSubmit}
                        >
                          Generate Checklist
                        </Button>
                      </Grid>

                      <Grid item>
                        <Button
                          variant="contained"
                          size="small"
                          sx={{
                            fontSize: "10px",

                            backgroundColor: "#501e75",
                          }}
                          onClick={this.handleReset}
                        >
                          Reset
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                {/* RIGHT COLUMN */}

                <Grid item lg={displaysidebar ? 4.6 : 5.3} sm={12}>
                  {showPrimaryUser && (
                    <Grid item lg={12} sm={12} marginTop={1}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "12px",
                        }}
                      >
                        Secondary User
                      </Typography>

                      <Autocomplete
                        options={csrs.map((user) => ({
                          label: user.vchEmail,
                          id: user.ID,
                        }))}
                        size="small"
                        getOptionLabel={(option) =>
                          option?.label || ""
                        }
                        value={
                          csrs
                            .filter(
                              (user) =>
                                user.ID ===
                                selectedSecondaryUser,
                            )
                            .map((user) => ({
                              label: user.vchEmail,
                              id: user.ID,
                            }))[0] || null
                        }
                        onChange={(_event, newValue) =>
                          this.handleSecondaryUserSelectionChange(
                            newValue,
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Select User..."
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              style: {
                                height: 30,
                                fontSize: "11px",
                              },
                            }}
                          />
                        )}
                        fullWidth
                        slotProps={{
                          listbox: {
                            sx: {
                              fontSize: "11px",
                            },
                          },
                        }}
                      />

                      {errors.secondaryUser && (
                        <span
                          style={{
                            color: "red",
                            fontSize: "smaller",
                          }}
                        >
                          {errors.secondaryUser}
                        </span>
                      )}
                    </Grid>
                  )}
                  {/* Select Checklist */}

                  <Grid item lg={12} sm={12} marginTop={1}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "12px",
                      }}
                    >
                      Select Checklist{" "}
                      <span
                        style={{
                          color: "red",

                          fontSize: "14px",

                          fontWeight: "bold",
                        }}
                      >
                        *
                      </span>
                    </Typography>

                    <Select
                      id="checklist"
                      value={selectedChecklist}
                      onChange={this.handleChecklistChange}
                      displayEmpty
                      fullWidth
                      sx={{
                        height: "30px",

                        "& .MuiOutlinedInput-input": {
                          fontSize: "12px",
                        },
                      }}
                    >
                      {checklistTypes.map((item) => (
                        <MenuItem
                          key={item.ID}
                          value={item.VCH_CHECKLIST}
                          sx={{
                            fontSize: "12px",
                          }}
                        >
                          {item.VCH_CHECKLIST}
                        </MenuItem>
                      ))}
                    </Select>

                    {errors.checklist && (
                      <span
                        style={{
                          color: "red",

                          fontSize: "smaller",
                        }}
                      >
                        {errors.checklist}
                      </span>
                    )}
                  </Grid>

                  {/* Client Name */}

                  <Grid item lg={12} sm={12} marginTop={0.5}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "12px",
                      }}
                    >
                      Client Name
                    </Typography>

                    <input
                      type="text"
                      value={clientName}
                      onChange={this.handleClientNameChange}
                      placeholder="Client Name"
                      maxLength={150}
                      style={{
                        width: "100%",
                        height: "30px",
                        fontSize: "12px",
                        padding: "5px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        outline: "none",
                      }}
                    />

                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "10px",
                        color: "gray",
                      }}
                    >
                      Maximum {clientName.length}/150 Characters
                    </Typography>
                  </Grid>

                  {/* Select Source Document

                  <Grid item lg={12} sm={12} marginTop={0.5}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "12px",
                      }}
                    >
                      Select Source Document{" "}
                      <span
                        style={{
                          color: "red",

                          fontSize: "14px",

                          fontWeight: "bold",
                        }}
                      >
                        *
                      </span>
                    </Typography>

                    {this.renderFileUpload(
                      "Acord Application",

                      "Application Accord",

                      true,
                    )}

                    {errors.sourceDoc && (
                      <span
                        style={{
                          color: "red",

                          fontSize: "smaller",
                        }}
                      >
                        {errors.sourceDoc}
                      </span>
                    )}
                  </Grid> */}

                  {/* BUTTONS

                  <Grid item lg={12} sm={12} marginTop={2}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item>
                        <Button
                          variant="contained"
                          size="small"
                          sx={{
                            fontSize: "10px",

                            backgroundColor: "#501e75",
                          }}
                          onClick={this.handleSubmit}
                        >
                          Generate Checklist
                        </Button>
                      </Grid>

                      <Grid item>
                        <Button
                          variant="contained"
                          size="small"
                          sx={{
                            fontSize: "10px",

                            backgroundColor: "#501e75",
                          }}
                          onClick={this.handleReset}
                        >
                          Reset
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid> */}
                </Grid>
              </Grid>
            </div>
          </div>

          {/* VALIDATION DIALOG */}

          <Dialog open={isMandatory} onClose={this.handleCloseConfirmDialog}>
            <DialogContent
              sx={{
                width: "370px",
                padding: "20px 25px",
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "#FEE2E2",
                    borderRadius: "50%",
                    width: 60,
                    height: 60,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span
                    style={{
                      color: "#B91C1C",
                      fontSize: 50,
                    }}
                  >
                    *
                  </span>
                </Box>
              </Box>

              <DialogTitle
                sx={{
                  textAlign: "center",
                  fontWeight: "bold",
                  fontSize: "1.25rem",
                  p: 0,
                }}
              >
                ⚠️ Warning
              </DialogTitle>

              <DialogContentText
                sx={{
                  textAlign: "center",
                  fontWeight: "bold",
                  mt: 1,
                  mb: 3,
                }}
              >
                {mandatoryMessage}
              </DialogContentText>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Button
                  onClick={this.handleCloseConfirmDialog}
                  sx={{
                    color: "#007bff",
                    border: "0.5px solid #53494987",
                    px: 4,
                    borderRadius: "6px",
                  }}
                >
                  Close
                </Button>
              </Box>
            </DialogContent>
          </Dialog>

          {/* SNACKBAR */}

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={2500}
            onClose={this.handleCloseSnackbar}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Alert
              severity={snackbarSeverity}
              onClose={this.handleCloseSnackbar}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </GridTable1>
      </>
    );
  }
}

export default EndorsementCheckUploads;